<template>
  <div>
    <HeaderBreadcrumbs
      :items="[
        { label: 'Admin', to: '/user-admin' },
        { label: 'Add New' }
      ]"
      title="New Admin"
    />

    <div class="flex-col w-full mb-3 bg-white rounded-lg">
      <div class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg">
        <div class="flex flex-col w-1/2">
          <FormLabel htmlFor="email">
            Email
          </FormLabel>

          <TextInput
            type="email"
            id="email"
            placeholder="Enter Email"
            :variant="((isSubmitted && !form.email.trim())) || emailAlreadyExists ? 'error' : 'default'"
            v-model="form.email"
          />

          <div v-if="isSubmitted && !form.email.trim()" class="mt-1 text-sm text-red-500">Email is required.</div>
          <div v-if="isSubmitted && form.email.trim() && emailAlreadyExists" class="mt-1 text-sm text-red-500">Email already exists.</div>

          <FormLabel htmlFor="password" className="mt-3">
            Password
          </FormLabel>

          <div class="relative">
            <TextInput
              :type="showPassword ? 'text' : 'password'"
              id="password"
              disabled
              placeholder="Enter Password"
              class="w-full pr-10 text-gray-400"
              :variant="isSubmitted && isPasswordError ? 'error' : 'default'"
              v-model="form.password"
              
            />
            <button
              type="button"
              @click="toggleShowPassword"
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              <EyeFilled v-if="!showPassword" className="w-4 h-4" />
              <EyeSlashFilled v-else className="w-4 h-4" />
            </button>
          </div>

          <div v-if="isSubmitted && isPasswordError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="group" className="mt-3">
            Group
          </FormLabel>

          <SelectRadio
            id="group"
            defaultText="Select Group"
            v-model="form.group"
            :options="groupOptions"
            @input="changeGroup"
            :isError="isSubmitted && isGroupError"
          />

          <!-- <div v-if="isSubmitted && isGroupError" class="mt-1 text-sm text-red-500">Must be filled.</div> -->

          <div v-if="form.group === 'publisher'">
            <FormLabel htmlFor="vendor" className="mt-3">
              Publisher
            </FormLabel>

            <multiselect
              v-model="form.vendor"
              label="name"
              track-by="name"
              placeholder="Type to find publisher"
              open-direction="bottom"
              class="box-border custom-multiselect"
              :class="isSubmitted && isVendorError ? 'error' : ''"
              :options="vendorOptions"
              :searchable="true"
              :loading="isLoadingVendor"
              :close-on-select="true"
              @search-change="handleSearchVendor"
            >
              <span slot="noResult">
                Oops! No elements found. Consider changing the search query.
              </span>
            </multiselect>

            <div v-if="isSubmitted && isVendorError" class="mt-1 text-sm text-red-500">Must be filled.</div>
          </div>
          <div v-if="form.group === 'publisher_group'">
            <FormLabel htmlFor="vendor" className="mt-3">
              Publisher Group
            </FormLabel>

            <multiselect
              v-model="form.vendor_group"
              label="name"
              track-by="name"
              placeholder="Type to find publisher"
              open-direction="bottom"
              class="box-border custom-multiselect"
              :class="isSubmitted && isVendoGroupError ? 'error' : ''"
              :options="vendorGroupOption"
              :searchable="true"
              :loading="isLoadingVendorGroup"
              :close-on-select="true"
              @search-change="handleSearchVendorGroup"
            >
              <span slot="noResult">
                Oops! No elements found. Consider changing the search query.
              </span>
            </multiselect>
          </div>
            <div v-if="isSubmitted && isVendoGroupError" class="mt-1 text-sm text-red-500">Must be filled.</div>
           <FormLabel htmlFor="role" className="mt-3">
            Role ID
          </FormLabel>

          <SelectRadio
            id="role"
            defaultText="Select Role"
            :disabled="!form.group"
            v-model="form.role"
            :options="roleOption"
            :class="!form.group ? 'opacity-50' : 'opacity-100'"
            :isError="isSubmitted && isRoleError"
          />

          <div v-if="isSubmitted && isRoleError" class="mt-1 text-sm text-red-500">Must be filled.</div>

          <FormLabel htmlFor="status" className="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.isActive"/>

          <div class="flex gap-5 mt-4">
            <Button @click="submitForm" :disabled="isLoadingCreate">
              {{ isLoadingCreate ? 'Saving...' : 'Save' }}
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="cancelCreate"
            >
              Cancel
            </Button>
          </div>

        </div>
      </div>
    </div>

    <!-- Confirmation Modal for existing admin assignment -->
    <ConfirmationModal
      :visible.sync="showConfirmation"
      :admin-count="existingAdminCount"
      :vendor-name="form.vendor?.name"
      @continue="proceedWithCreation"
      @cancel="cancelCreation"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import FormLabel from '@/components/ui/FormLabel';
import GmSwitch from '@/components/ui/Switch';
import TextInput from '@/components/ui/TextInput';
import Button from '@/components/ui/Button';
import SelectRadio from '@/components/ui/SelectRadio';
import EyeFilled from '@/components/icons/EyeFilled';
import EyeSlashFilled from '@/components/icons/EyeSlashFilled';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import { EventBus } from '@/event-bus';

import constant from '@/store/constant';

//const passwordDefault = process.env.VUE_APP_PASSWORD_DEFAULT_USERADMIN;

export default {
  name: 'UserAdminCreateNew',
  components: {
    HeaderBreadcrumbs,
    FormLabel,
    GmSwitch,
    TextInput,
    Button,
    SelectRadio,
    EyeFilled,
    EyeSlashFilled,
    ConfirmationModal
  },
  data() {
    return {
      showPassword: false,
      form: {
        email: '',
        password: 'KitaGramediaDigial',
        role: null,
        group: null,
        vendor: null,
        vendor_group:null,
        isActive: true
      },

      roleOptions: [],
      roleOption: [],  
      vendorOptions: [],
      vendorGroupOption: [],
      isLoadingVendor: false,
      isLoadingVendorGroup:false,
      groupOptions: [
        { value: 'ebook', text: 'Ebook' },
        { value: 'publisher', text: 'Publisher' },
        { value: 'publisher_group', text: 'Publisher Group' }
      ],

      isLoadingCreate: false,
      isSubmitted: false,
      emailAlreadyExists: false, // Added state for email existence check
      userData: JSON.parse(localStorage.getItem('session_auth')),
      // Confirmation modal data
      showConfirmation: false,
      existingAdminCount: 0,
      pendingPayload: null
    }
  },
  computed: {
    ...mapState({
      successMessage: (state) => state.admin.successMessage,
      errorMessage: (state) => state.admin.errorMessage,
      itemRole: (state) => state.accessRole.items
    }),
    isEmailError() {
      return !this.form.email.trim() || this.emailAlreadyExists; // Use the new state
    },
    isPasswordError() {
      return !this.form.password.trim();
    },
    isRoleError() {
      return !this.form.role;
    },
    isGroupError() {
      return !this.form.group;
    },
    isVendorError() {
      // Vendor is not required for ebook group (internal admins)
      if (this.form.group === 'ebook') {
        return false;
      }
      return !this.form.vendor;
    },
      isVendoGroupError() {
      if (this.form.group === 'ebook' || this.form.group === 'publisher') {
        return false;
      }
      return !this.form.vendor_group;
    }
  },
  watch: {
    successMessage: function(newVal) { // Added explicit parameter name
      if (!newVal) return;
      EventBus.$emit('show-toast', {
        message: newVal,
        type: 'success'
      });
      this.$router.push('/user-admin');
    },
    errorMessage: function(newVal) { 
      if (!newVal) return;

      if (newVal === 'Email is already exist') {
        this.emailAlreadyExists = true;
        this.$store.commit('admin/setErrorMessage', null);
        return;
      }

      EventBus.$emit('show-toast', {
        message: newVal,
        type: 'error'
      });
    },
  },
  mounted() {
    this.fetchRole();
  },
  methods: {
    ...mapActions('vendors', ['findVendor','fetchPublisherGroup']),
    ...mapActions('accessRole', ['fetchAllRoleNew']),

    fetchRole() {
      this.fetchAllRoleNew().then((res) => {
        for (let item in res) {
          this.roleOptions.push({
            value: res[item].id,
            text: res[item].name.replace(/\b\w/g, l => l.toUpperCase())
          });
        }
      });
    },
    handleSearchVendorGroup(key) {
      if(key.length > 2) {
        this.isLoadingVendorGroup = true;
        const payload = {
            q:key,
             is_active:true
        }
         this.fetchPublisherGroup(payload)
          .then((resp) => {
            this.vendorGroupOption = resp.data.data.rows;
            this.isLoadingVendorGroup = false;
          })
          .catch((err) => {
            this.isLoadingVendorGroup = false;
            console.error(err);
          });
      }
    },
    handleSearchVendor(keyword) {
      if (keyword.length > 2) {
        this.isLoadingVendor = true;
        const payload = {
          q: keyword
        };
        this.findVendor(payload)
          .then((resp) => {
            this.vendorOptions = resp.data.data;
            this.isLoadingVendor = false;
          })
          .catch((err) => {
            this.isLoadingVendor = false;
            console.error(err);
          });
      } else if (keyword.length === 0) {
        this.vendorOptions = [];
      }
    },
    changeGroup(selectedValue) {
    const mapping = {
      ebook: null,
      publisher: constant.ALLOWED_ROLE_WITH_VENDOR,
      publisher_group: constant.ALLOWED_ROLE_WITH_VENDOR_GROUP
    };

    const allowed = mapping[selectedValue];
    this.roleOption = allowed
      ? this.roleOptions.filter(r => allowed.includes(r.value))
      : this.roleOptions; 
    },   
    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },

    validateForm() {
      this.emailAlreadyExists = false;

      const isEmailValid = this.form.email.trim() && !this.emailAlreadyExists;
      const isPasswordValid = this.form.password.trim();
      const isRoleValid = this.form.role !== null && this.form.role !== undefined;
      const isGroupValid = this.form.group !== null && this.form.group !== undefined;
      // Vendor is not required for ebook group (internal admins)
      const isVendorValid = this.form.group === 'ebook' ||
       (this.form.vendor !== null && this.form.vendor !== undefined) || 
       (this.form.vendor_group !== null && this.form.vendor_group !== undefined) ;

      return isEmailValid && isPasswordValid && isRoleValid && isGroupValid && isVendorValid;
    },

    async submitForm() {
      this.isSubmitted = true;
      this.emailAlreadyExists = false; // Reset email error state

      if (!this.validateForm()) {
        return;
      }

      this.isLoadingCreate = true;

       const payload = {
          email: this.form.email,
          password: this.form.password,
          role_id: this.form.role,
          group: this.form.group,
          is_active: this.form.isActive,
        };

        // Tambahkan publisher atau publisher_group sesuai kondisi
        if (payload.group === 'publisher' && this.form.vendor) {
          payload.vendor = this.form.vendor.id;
        } 
        if (payload.group === 'publisher_group' && this.form.vendor_group) {
          payload.vendor_group = this.form.vendor_group.id;
        }
      if (this.form.group !== 'ebook' && this.form.vendor) {
        try {
          await this.$store.dispatch('admin/fetchAdmin', { vendor: this.form.vendor.id });
          this.existingAdminCount = this.$store.state.admin.totalRows || 0;

          if (this.existingAdminCount > 0) {
            this.pendingPayload = payload;
            this.showConfirmation = true;
            this.isLoadingCreate = false;
            return;
          }
        } catch (error) {
          console.error('Error checking existing admins:', error);
        }
      }

      await this.$store.dispatch('admin/createUserAdmin', payload);
      this.isLoadingCreate = false;
    },

    async fetchAdmin({ vendor = null } = {}) {
      try {
        await this.$store.dispatch('admin/fetchAdmin', { vendor });
      } catch (error) {
        console.error('Error fetching admin list:', error);
      }
    },

    async proceedWithCreation() {
      if (!this.pendingPayload) return;

      this.isLoadingCreate = true;

      try {
        const payloadWithForce = {
          ...this.pendingPayload,
          force: true
        };

        await this.$store.dispatch('admin/createUserAdmin', payloadWithForce);
      } catch (error) {
        console.error('Error forcing user admin creation:', error);
        EventBus.$emit('show-toast', {
          message: this.errorMessage || 'Failed to create admin even with force.',
          type: 'error'
        });
      } finally {
        this.isLoadingCreate = false;
        this.pendingPayload = null;
        this.showConfirmation = false;
      }
    },

    cancelCreation() {
      this.pendingPayload = null;
      this.isLoadingCreate = false;
      this.showConfirmation = false;
    },

    cancelCreate() {
      this.$router.push('/user-admin');
    }
  }
}
</script>

<style scoped>
.error input {
  border-color: #f87171; /* Tailwind's red-500 */
  box-shadow: 0 0 0 1px #f87171;
}

.custom-multiselect.error >>> .multiselect__tags {
  border-color: #f87171;
}
</style>
